/**
 * @file CopyButton.tsx
 * @description Кнопка «Копировать» значения из инструкции (phase19.28
 * этап А; uiux-platform-shops §8 «CopyField»): копирует в буфер обмена и
 * объявляет «Скопировано» через `aria-live`, значок на 2 с меняется на
 * галочку. Буфер недоступен (старый браузер, не https) — toast с
 * подсказкой выделить значение вручную.
 */

import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";

interface Props {
  value: string;
  /** Что копируем — для подписи скринридера: «Копировать IP-адрес». */
  label: string;
  /** С подписью «Копировать» (по умолчанию — только значок). */
  withText?: boolean;
}

export function CopyButton({ value, label, withText = false }: Props) {
  const notify = useNotify();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      notify.error("Не удалось скопировать — выделите значение и скопируйте вручную");
    }
  }

  const Icon = copied ? Check : Copy;
  return (
    <>
      <Button
        type="button"
        variant={withText ? "outline" : "ghost"}
        size={withText ? "default" : "icon"}
        onClick={() => void copy()}
        aria-label={withText ? undefined : `Копировать: ${label}`}
        title={`Копировать: ${label}`}
      >
        <Icon className="size-4" aria-hidden />
        {withText ? label : null}
      </Button>
      <span className="sr-only" aria-live="polite">
        {copied ? "Скопировано" : ""}
      </span>
    </>
  );
}
